import {
  AuthPageBottomContentWrapper,
  AuthPageContentWrapper,
  AuthPageErrorWrapper,
  AuthPageFieldsWrapper,
  AuthPageMainContentWrapper,
  AuthPageWrapper,
  Form,
} from '@pages/AuthPage.styles';
import {Link, useLocation, useNavigate} from 'react-router';
import Text from '@atoms/Text';
import {theme} from '@styles/theme';
import {ReactComponent as Logo} from '@assets/logo.svg';
import Input from '@atoms/Input';
import {useEffect, useState} from 'react';
import Button from '@atoms/Button';
import {ViewFilled, ViewOffFilled, WarningFilled} from '@carbon/icons-react';
import {ERoutes} from '@constants/routes';
import {Formik} from 'formik';
import {authByEmail} from '@store/slices/users';
import {useDispatch, useSelector} from 'react-redux';
import {AppDispatch, RootState} from '@store';
import {IDLE} from '@constants/loadingState';
import {setErrors, removeErrorsList} from '@store/slices/common';

export const AuthPage = () => {
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const navigate = useNavigate();
  const from = location.state?.from || ERoutes.MAIN;
  const [passwordFieldType, setPasswordFieldType] = useState<'password' | 'text'>(
    'password',
  );
  const [didSubmit, setDidSubmit] = useState(false);
  const {error, errors_list} = useSelector((state: RootState) => state.common);
  const {loading, token} = useSelector((state: RootState) => state.users);

  const handleSubmit = (values: {email: string; password: string}) => {
    setDidSubmit(true);
    dispatch(authByEmail(values));
  };

  const handleSuccessAuth = () => {
    navigate(from, {replace: true});
  };

  useEffect(() => {
    if (token && loading === IDLE) {
      handleSuccessAuth();
    }
  }, [token]);

  useEffect(() => {
    if (didSubmit && loading === IDLE && !token) {
      dispatch(setErrors({error: 'Логин и/или пароль введены неверно'}));
    }
  }, [didSubmit, loading, token]);

  return (
    <AuthPageWrapper>
      <AuthPageContentWrapper>
        <AuthPageMainContentWrapper>
          <Logo />
          <Text text="Авторизация" type="titleR" />
          <Formik
            enableReinitialize
            initialValues={{email: '', password: ''}}
            onSubmit={handleSubmit}>
            {({handleSubmit, values, setFieldValue, touched, errors}) => {
              return (
                <Form onSubmit={handleSubmit}>
                  <AuthPageFieldsWrapper>
                    <Input
                      id="login"
                      label="Логин"
                      value={values.email}
                      onChange={e => {
                        dispatch(setErrors({error: ''}));
                        dispatch(removeErrorsList());
                        setFieldValue('email', e.currentTarget.value);
                      }}
                      error={
                        (touched.email && errors.email ? errors.email : '') ||
                        (error ? String(error) : '') ||
                        (errors_list.length > 0 ? 'auth_error' : '')
                      }
                      withoutErrorText
                    />
                    <Input
                      id="password"
                      label="Пароль"
                      type={passwordFieldType}
                      value={values.password}
                      onChange={e => {
                        dispatch(setErrors({error: ''}));
                        dispatch(removeErrorsList());
                        setFieldValue('password', e.currentTarget.value);
                      }}
                      iconElement={
                        passwordFieldType == 'password' ? (
                          <ViewOffFilled
                            size={16}
                            color={
                              values.password
                                ? theme.colors.basic02
                                : theme.colors.basic03
                            }
                          />
                        ) : (
                          <ViewFilled
                            size={16}
                            color={
                              values.password
                                ? theme.colors.basic02
                                : theme.colors.basic03
                            }
                          />
                        )
                      }
                      onClickIcon={() => {
                        if (values.password) {
                          setPasswordFieldType(
                            passwordFieldType == 'password' ? 'text' : 'password',
                          );
                        }
                      }}
                      withoutErrorText
                      error={
                        (touched.password && errors.password ? errors.password : '') ||
                        (error ? String(error) : '') ||
                        (errors_list.length > 0 ? 'auth_error' : '')
                      }
                    />
                    {(error || !!errors_list.length) && (
                      <AuthPageErrorWrapper>
                        <WarningFilled size={14} fill={theme.colors.signalRed00} />
                        <Text
                          text="Логин и/или пароль введены неверно"
                          type="tinyR"
                          color={theme.colors.signalRed00}
                        />
                      </AuthPageErrorWrapper>
                    )}
                    <Link to={ERoutes.AUTH_RECOVERY}>
                      <Text text="Забыли пароль?" color={theme.colors.accent00} />
                    </Link>
                  </AuthPageFieldsWrapper>
                  <Button
                    title="Войти"
                    variant="filled"
                    type="submit"
                    size="big"
                    fullWidth
                    disabled={
                      !values.email || !values.password || !!error || !!errors_list.length
                    }
                  />
                </Form>
              );
            }}
          </Formik>
        </AuthPageMainContentWrapper>
        <AuthPageBottomContentWrapper>
          <Link to="/#">
            <Text text="Авторизоваться через OAuth" color={theme.colors.accent00} />
          </Link>
        </AuthPageBottomContentWrapper>
      </AuthPageContentWrapper>
    </AuthPageWrapper>
  );
};

export default AuthPage;
